import { create } from "zustand";

export interface AdminTab {
    path: string;
    name: string;
    title: string;
    closable?: boolean;
    isKeepAlive?: boolean;
}

export type TabSide = "left" | "right";

export interface ReactTabsStore {
    tabs: AdminTab[];
    activePath: string;
    setTabs: (tabs: AdminTab[]) => void;
    setActivePath: (path: string) => void;
    addTab: (tab: AdminTab) => void;
    removeTab: (path: string) => string | undefined;
    closeTabsOnSide: (path: string, side: TabSide) => void;
    closeMultipleTabs: (path: string) => void;
    closeAllTabs: () => void;
}

const isSameTab = (left: AdminTab, right: AdminTab) =>
    left.path === right.path || left.name === right.name || left.title === right.title;

/** React 标签页 Store，保留旧 Vue tabs 的去重、邻接切换和固定页策略。 */
export const useTabsStore = create<ReactTabsStore>((set, get) => ({
    tabs: [],
    activePath: "",
    setTabs: tabs =>
        set({
            tabs,
            activePath: tabs.find(tab => tab.path === get().activePath)?.path || tabs[0]?.path || ""
        }),
    setActivePath: activePath => set({ activePath }),
    addTab: tab => {
        const currentTabs = get().tabs;
        const duplicateIndex = currentTabs.findIndex(item => isSameTab(item, tab));
        const tabs = [...currentTabs];
        if (duplicateIndex >= 0) {
            tabs[duplicateIndex] = tab;
        } else {
            tabs.push(tab);
        }
        set({ tabs, activePath: tab.path });
    },
    removeTab: path => {
        const currentTabs = get().tabs;
        const index = currentTabs.findIndex(tab => tab.path === path);
        if (index < 0) {
            return undefined;
        }

        const nextTab = currentTabs[index + 1] || currentTabs[index - 1];
        const tabs = currentTabs.filter(tab => tab.path !== path);
        set({ tabs, activePath: get().activePath === path ? nextTab?.path || tabs[0]?.path || "" : get().activePath });
        return nextTab?.path;
    },
    closeTabsOnSide: (path, side) => {
        const tabs = get().tabs;
        const currentIndex = tabs.findIndex(tab => tab.path === path);
        if (currentIndex < 0) {
            return;
        }

        const filtered = tabs.filter((tab, index) => {
            if (!tab.closable) {
                return true;
            }
            return side === "left" ? index >= currentIndex : index <= currentIndex;
        });
        set({ tabs: filtered, activePath: get().activePath });
    },
    closeMultipleTabs: path => {
        const tabs = get().tabs.filter(tab => !tab.closable || tab.path === path);
        set({ tabs, activePath: tabs.some(tab => tab.path === get().activePath) ? get().activePath : path });
    },
    closeAllTabs: () => {
        const tabs = get().tabs.filter(tab => !tab.closable);
        set({ tabs, activePath: tabs[0]?.path || "" });
    }
}));
